import { useEffect, useState } from "react";
import { CheckCircle2, RotateCcw, Volume2, Loader2, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import GameShell, { shuffle, pickWeighted } from "./GameShell";

/**
 * Blending Game — the learner sees N phoneme tiles (b · a · t), taps each
 * to hear the sound, then taps "Blend" to hear them slide together into
 * the real word. They pick the correct word from three options.
 *
 * Progression:
 *  • Level 1 = CVC words only. The learner MUST hit 5-in-a-row to unlock L2.
 *  • Level 2 = adds digraphs + long-vowel patterns (ship, boat, cake…).
 *
 * The unlock is server-authoritative: the client posts its streak, the
 * backend flips `blending_cvc_mastered` on the user, and future GETs
 * return Level-2 items automatically.
 */
export default function BlendingGame() {
    const [items, setItems] = useState([]);
    const [level, setLevel] = useState(1);
    const [mastered, setMastered] = useState(false);
    const [masteryPct, setMasteryPct] = useState(80);
    const [round, setRound] = useState(null);
    const [correct, setCorrect] = useState(0);
    const [total, setTotal] = useState(0);
    const [streak, setStreak] = useState(0);
    const [picked, setPicked] = useState(null);
    const [done, setDone] = useState(false);
    const [busyGrapheme, setBusyGrapheme] = useState(null);
    const [blendState, setBlendState] = useState("idle"); // idle | loading | playing | played

    // Load items (respecting the learner's level).
    useEffect(() => {
        loadItems(1);
    }, []);

    const loadItems = async (wantLevel) => {
        const { data } = await api.get(`/games/blending?level=${wantLevel}`);
        setItems(data.items);
        setLevel(data.level);
        setMastered(!!data.cvc_mastered);
        setMasteryPct(data.mastery_pct || 80);
        nextRound(data.items);
    };

    const nextRound = (source) => {
        const target = pickWeighted(source) || source[0];
        setRound({ target, options: shuffle(target.options) });
        setPicked(null);
        setBlendState("idle");
    };

    const playPhoneme = async (g) => {
        if (busyGrapheme) return;
        setBusyGrapheme(g);
        try {
            const { data } = await api.post("/tts", { text: g, mode: "phoneme" });
            await new Audio(`data:${data.mime};base64,${data.audio_base64}`).play().catch(() => {});
        } catch {
            /* silent — learner can retry */
        } finally {
            setBusyGrapheme(null);
        }
    };

    const playBlend = async () => {
        if (blendState === "loading" || blendState === "playing") return;
        setBlendState("loading");
        try {
            const { data } = await api.post("/tts", { text: round.target.word, mode: "sound_out" });
            const audio = new Audio(`data:${data.mime};base64,${data.audio_base64}`);
            setBlendState("playing");
            audio.onended = () => setBlendState("played");
            audio.onerror = () => setBlendState("played");
            await audio.play().catch(() => setBlendState("played"));
        } catch {
            setBlendState("idle");
        }
    };

    const pick = async (opt) => {
        if (picked) return;
        setPicked(opt);
        const ok = opt === round.target.word;
        const newTotal = total + 1;
        const newCorrect = correct + (ok ? 1 : 0);
        const newStreak = ok ? streak + 1 : 0;
        setTotal(newTotal);
        setCorrect(newCorrect);
        setStreak(newStreak);

        if (ok) {
            toast.success(`Yes — that's "${round.target.word}"`);
            // Streak-based unlock still supported: fires as soon as they hit 5.
            if (!mastered && newStreak >= 5) {
                try {
                    const { data } = await api.post("/games/blending/mastery", { streak: newStreak });
                    if (data.just_unlocked) {
                        toast.success("Level 2 unlocked — vowel teams next!", { duration: 4000 });
                        const { data: r } = await api.get("/games/blending?level=2");
                        setItems(r.items);
                        setLevel(r.level);
                        setMastered(true);
                    }
                } catch {
                    /* non-fatal */
                }
            }
        } else {
            toast.error(`Almost — the sounds blend into "${round.target.word}"`);
            // Adaptive difficulty: log every grapheme in the missed word so
            // the next reload weights them higher. Fire-and-forget.
            (round.target.graphemes || []).forEach((g) => {
                api.post("/games/grapheme-miss", { grapheme: g }).catch(() => {});
            });
        }

        setTimeout(() => {
            if (newTotal >= 10) {
                finish(newCorrect);
            } else {
                nextRound(items);
            }
        }, 1000);
    };

    const finish = async (finalCorrect) => {
        setDone(true);
        const accuracyPct = Math.round((finalCorrect / 10) * 100);
        try {
            await api.post("/progress/game-score", {
                game: "blending",
                score: finalCorrect * 6,
                correct: finalCorrect,
                total: 10,
            });
            toast.success(`+${finalCorrect * 6} XP earned`);
        } catch {
            /* non-fatal */
        }
        // Graded-assignment mastery: 8/10 (or better) unlocks Level 2 too.
        if (!mastered) {
            try {
                const { data } = await api.post("/games/blending/mastery", {
                    correct: finalCorrect,
                    total: 10,
                });
                if (data.just_unlocked) {
                    toast.success(
                        `${accuracyPct}% — you passed! Level 2 unlocked.`,
                        { duration: 4200 },
                    );
                    setMastered(true);
                }
            } catch {
                /* non-fatal */
            }
        }
    };

    const reset = () => {
        setCorrect(0); setTotal(0); setStreak(0); setDone(false); nextRound(items);
    };

    if (!round)
        return (
            <GameShell title="Blending">
                <p>Loading…</p>
            </GameShell>
        );

    return (
        <GameShell
            title="Blending"
            intro="Hear each sound, then blend them together to find the word."
            testId="blending-game"
            instructions="This is the Blending game. You will see three sound tiles at the top %%[data-testid='blending-tiles']%%. Tap each tile to hear its sound. Then tap the Blend button to hear the sounds slide together into one word %%[data-testid='blending-blend-button']%%. When you know the word, tap the correct answer from the three choices below %%[data-testid='blending-options']%%. Ten rounds total. Score 80 percent or better to unlock Level 2."
        >
            <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
                <div className="flex items-center gap-3">
                    <p className="text-sm text-[#819888]">Round {done ? total : total + 1} of 10</p>
                    <span
                        className="text-xs px-2 py-1 rounded font-medium"
                        style={{
                            background: level === 2 ? "#FDE9DD" : "#E0EDE6",
                            color: level === 2 ? "#B86644" : "#2D5D4E",
                        }}
                        data-testid="blending-level-badge"
                    >
                        Level {level} · {level === 2 ? "Vowel teams" : "CVC"}
                    </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                    <span className="text-[#2D5D4E] font-medium" data-testid="blending-score">
                        Score: {correct} / {total}
                    </span>
                    {streak >= 2 && (
                        <span
                            className="inline-flex items-center gap-1 text-[#D47A55] font-medium"
                            data-testid="blending-streak"
                        >
                            <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
                            {streak} in a row
                        </span>
                    )}
                </div>
            </div>

            {done ? (
                <div className="card-rw p-10 text-center">
                    <CheckCircle2 className="w-12 h-12 text-[#3A7D5C] mx-auto mb-4" strokeWidth={1.6} />
                    <h2 className="text-2xl font-medium mb-2" style={{ fontFamily: "Outfit" }}>
                        Round complete
                    </h2>
                    <p className="text-lg text-[#515B57] mb-2">
                        You got {correct} out of {total} correct — {Math.round((correct / Math.max(total, 1)) * 100)}%.
                    </p>
                    {!mastered && (
                        <p className="text-sm text-[#819888] mb-6" data-testid="blending-mastery-hint">
                            Score {masteryPct}% or better to unlock Level 2.
                        </p>
                    )}
                    {mastered && total > 0 && (
                        <p className="text-sm text-[#2D5D4E] font-medium mb-6" data-testid="blending-mastered-badge">
                            Level 2 unlocked · vowel teams and digraphs
                        </p>
                    )}
                    <button
                        onClick={reset}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237]"
                        data-testid="blending-replay"
                    >
                        <RotateCcw className="w-4 h-4" /> Play again
                    </button>
                </div>
            ) : (
                <>
                    {/* Phoneme tile row */}
                    <div className="card-rw p-8 md:p-10 mb-6" data-testid="blending-tiles">
                        <p className="label-eyebrow mb-5 text-center">Tap each sound</p>
                        <div className="flex items-center justify-center gap-3 md:gap-4 flex-wrap mb-6">
                            {round.target.graphemes.map((g, i) => (
                                <div key={i} className="flex items-center gap-2 md:gap-3">
                                    <button
                                        onClick={() => playPhoneme(g)}
                                        disabled={!!busyGrapheme}
                                        data-testid={`blending-phoneme-${i}`}
                                        aria-label={`Play the sound of ${g}`}
                                        className="w-20 h-20 md:w-24 md:h-24 rounded-2xl border-2 border-[#2D5D4E] bg-white text-[#1C2321] text-3xl md:text-4xl font-semibold flex flex-col items-center justify-center hover:bg-[#E0EDE6] transition-colors disabled:opacity-60"
                                        style={{ fontFamily: "Outfit" }}
                                    >
                                        <span>{g.replace("_", "-")}</span>
                                        <span className="text-[#2D5D4E] mt-1">
                                            {busyGrapheme === g ? (
                                                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
                                            ) : (
                                                <Volume2 className="w-4 h-4" strokeWidth={1.8} />
                                            )}
                                        </span>
                                    </button>
                                    {i < round.target.graphemes.length - 1 && (
                                        <span className="text-2xl md:text-3xl text-[#819888]" aria-hidden>+</span>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="text-center">
                            <button
                                onClick={playBlend}
                                data-testid="blending-blend-button"
                                disabled={blendState === "loading" || blendState === "playing"}
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium hover:bg-[#1F4237] disabled:opacity-60 transition-all"
                            >
                                {blendState === "loading" || blendState === "playing" ? (
                                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
                                ) : (
                                    <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                                )}
                                Blend the sounds
                            </button>
                        </div>
                    </div>

                    {/* Answer options */}
                    <p className="label-eyebrow mb-3">Which word did you hear?</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="blending-options">
                        {round.options.map((opt) => {
                            const isPicked = picked === opt;
                            const isCorrect = picked && opt === round.target.word;
                            const cls = isCorrect
                                ? "tile tile-correct"
                                : isPicked
                                    ? "tile tile-wrong"
                                    : "tile";
                            return (
                                <button
                                    key={opt}
                                    onClick={() => pick(opt)}
                                    disabled={!!picked}
                                    data-testid={`blending-option-${opt}`}
                                    className={`${cls} py-6 text-3xl`}
                                    style={{ fontFamily: "Outfit" }}
                                >
                                    {opt}
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </GameShell>
    );
}
